import { Assets, Components, Enums, Services, Types, Utils } from '@sendum/core'
import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useDispatch } from 'react-redux'
import Select, { StylesConfig, ValueType } from 'react-select'

import * as PalletSelectorActions from '../store/PalletSelectorActions'
import { setPallets } from '../store/PalletSelectorActions'
import { renderTime } from '../utils'
import s from './Pallet.module.scss'
import styles from './MoveModal.module.scss'
import { AddExistingPhysicalPalletModal, IAddExistingPhysicalPalletModalRef } from './AddExistingPhysicalPalletModal'
import { ISetPalletFillingModalRef, SetPalletFillingModal } from './SetPalletFillingModal'

import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'

import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

type OptionType = { value: string; label: string }

export interface IMoveModalRef {
    open: (pallet: Types.Pallet) => Promise<void>
    close: () => void
}

interface IProps {
    onMoved: () => void
    isMovement?: boolean
}

type PhysicalPallet = {
    type: Enums.PalletType
    ppn: string
    palletFilling: Enums.PalletFillingType
    notes?: string
    newLocation?: string
}

export const MoveModal = forwardRef<IMoveModalRef, IProps>(
    ({ onMoved, isMovement }, ref): JSX.Element => {
        const dispatch = useDispatch()
        const [pallet, setPallet] = useState<Types.Pallet>()
        const [validNextLocationsOptions, setValidNextLocationsOptions] = useState<OptionType[]>()
        const [physicalPalletNumber, setPhysicalPalletNumber] = useState<string>('')
        const [samplePalletNumber, setSamplePalletNumber] = useState<string>('')
        const [physicalPalletNumbers, setPhysicalPalletNumbers] = useState<PhysicalPallet[]>([])
        const [physicalCart1Number, setPhysicalCart1Number] = useState<string>('')
        const [physicalCart2Number, setPhysicalCart2Number] = useState<string>('')
        const [newLocation, setNewLocation] = useState<string>()
        const [isLoading, setLoading] = useState<boolean>(false)
        const [actualDateTime, setActualDateTime] = useState<Date | undefined>(undefined)
        const [anotherPartOfPhysicalPallet, setAnotherPartOfPhysicalPallet] = useState<any | undefined>(undefined)

        const modalRef = useRef<Components.IModalRef>(null)
        const addExistingPhysicalPalletModalRef = useRef<IAddExistingPhysicalPalletModalRef>(null)
        const setPalletFillingModalRef = useRef<ISetPalletFillingModalRef>(null)

        const isShowPhysicalPalletNumber = useMemo(
            () =>
                (!pallet?.Location.RequestPhysicalNumber && !!pallet?.PalletPhysicalID) ||
                pallet?.Location.RequestPhysicalNumber === 'pallet' ||
                pallet?.Location.RequestPhysicalNumber === 'optional_pallet',
            [pallet]
        )
        const isShowPhysicalPalletNumbers = useMemo(() => pallet?.Location.RequestPhysicalNumber === 'pallets',
            [pallet]
        )
        const isShowSamplePalletNumber = useMemo(() => newLocation === 'Collect Sample', [newLocation])
        const isDisabledPhysicalPalletNumber = useMemo(
            () =>
                (!pallet?.Location.RequestPhysicalNumber && !!pallet?.PalletPhysicalID) ||
                (pallet?.Location.RequestPhysicalNumber === 'optional_pallet' && !!pallet?.PalletPhysicalID),
            [pallet]
        )
        const isShowPhysicalCart1Number = useMemo(
            () =>
                ((!pallet?.Location.RequestPhysicalNumber ||
                    pallet?.Location.RequestPhysicalNumber === 'optional_pallet') &&
                    pallet?.Cart1) ||
                pallet?.Location.RequestPhysicalNumber === 'carts',
            [pallet]
        )
        const isDisabledPhysicalCart1Number = useMemo(
            () =>
                (!pallet?.Location.RequestPhysicalNumber && !!pallet?.Cart1) ||
                pallet?.Location.RequestPhysicalNumber === 'optional_pallet',
            [pallet]
        )
        const isShowPhysicalCart2Number = useMemo(
            () =>
                ((!pallet?.Location.RequestPhysicalNumber ||
                    pallet?.Location.RequestPhysicalNumber === 'optional_pallet') &&
                    pallet?.Cart2) ||
                pallet?.Location.RequestPhysicalNumber === 'carts',
            [pallet]
        )
        const isDisabledPhysicalCart2Number = useMemo(
            () =>
                (!pallet?.Location.RequestPhysicalNumber && !!pallet?.Cart2) ||
                pallet?.Location.RequestPhysicalNumber === 'optional_pallet',
            [pallet]
        )

        const isDisabledMoveButton = useMemo<boolean>(() => {
            if (pallet?.Location.RequestPhysicalNumber === 'pallet' && !physicalPalletNumber) {
                return true
            }

            if (pallet?.Location.RequestPhysicalNumber === 'pallets' && !physicalPalletNumbers.every(_ => _.ppn)) {
                return true
            }

            if (pallet?.Location.RequestPhysicalNumber === 'pallets') {
                const PPNumbers = physicalPalletNumbers.map(_ => _.ppn)

                return PPNumbers.length !== [...new Set(PPNumbers)].length
            }

            if (
                pallet?.Location.RequestPhysicalNumber === 'carts' &&
                ((!physicalCart1Number && !physicalCart2Number) || physicalCart1Number === physicalCart2Number)
            ) {
                return true
            }

            if (isShowSamplePalletNumber && !samplePalletNumber) {
                return true
            }

            return isLoading || !newLocation
        }, [
            isLoading,
            newLocation,
            pallet?.Location.RequestPhysicalNumber,
            physicalPalletNumber,
            physicalCart1Number,
            physicalCart2Number,
            isShowSamplePalletNumber,
            samplePalletNumber,
            physicalPalletNumbers
        ])

        const palletTitle = useMemo(() => {
            return `${pallet?.Batch.BatchNumber} ${
                !pallet?.PalletNumber || pallet.PalletNumber === 'No Logical Pallets'
                    ? ''
                    : pallet.PalletNumber
            }`.trim()
        }, [pallet, pallet?.Batch.BatchNumber, pallet?.PalletNumber])

        const timeZone = useMemo(() => {
            return Utils.time.getTzString(pallet?.Location?.TimeZone)
        }, [pallet?.Location?.TimeZone])

        const minDateTime = useMemo(() => {
            if (!pallet?.Location?.LastActualEpochTime || !pallet?.Location?.TimeZone) {
                return undefined
            }
            return Utils.time.toDateTimePicker(pallet?.Location?.LastActualEpochTime + Utils.time.MILLISECONDS_OF_MIN, pallet?.Location?.TimeZone)
        }, [pallet?.Location?.LastActualEpochTime, pallet?.Location?.TimeZone])

        const maxDateTime = useMemo(() => {
            if (!pallet?.Location?.TimeZone) {
                return undefined
            }
            return Utils.time.toDateTimePicker(Utils.time.current(), pallet?.Location?.TimeZone)
        }, [pallet?.Location?.TimeZone, actualDateTime])

        const notification = useMemo(() => {
            if (!anotherPartOfPhysicalPallet || !pallet || isShowSamplePalletNumber || isShowPhysicalPalletNumbers) {
                return undefined
            }
            return `Logical pallets ${pallet.PalletNumber} and ${anotherPartOfPhysicalPallet.PalletNumber} are part of a pallet with physical number ${physicalPalletNumber}. Logical pallet ${anotherPartOfPhysicalPallet.PalletNumber} will be moved in the same way as the current pallet`
        }, [
            anotherPartOfPhysicalPallet,
            physicalPalletNumber,
            pallet,
            isShowSamplePalletNumber,
            isShowPhysicalPalletNumbers
        ])

        const move = useCallback(async () => {
            if (!pallet || !newLocation) {
                return
            }

            if (pallet?.Location.RequestPhysicalNumber === 'pallet' && !physicalPalletNumber) {
                return
            }
            if (pallet?.Location.RequestPhysicalNumber === 'pallets' && !physicalPalletNumbers.every(_ => _.ppn)) {
                return
            }

            if (pallet?.Location.RequestPhysicalNumber === 'pallets') {
                const PPNumbers = physicalPalletNumbers.map(_ => _.ppn)
                if (PPNumbers.length !== [...new Set(PPNumbers)].length) {
                    return
                }
            }

            if (
                pallet?.Location.RequestPhysicalNumber === 'carts' &&
                ((!physicalCart1Number && !physicalCart2Number) || physicalCart1Number === physicalCart2Number)
            ) {
                return
            }

            if (!checkAndRestoreActualDateTime()) {
                return
            }

            let PPN: string | undefined = undefined
            let PC1N: string | undefined = undefined
            let PC2N: string | undefined = undefined
            let physicalPallets: PhysicalPallet[] | undefined = undefined

            if (
                (!pallet?.Location.RequestPhysicalNumber ||
                    pallet?.Location.RequestPhysicalNumber === 'pallet' ||
                    pallet?.Location.RequestPhysicalNumber === 'optional_pallet') &&
                physicalPalletNumber
            ) {
                PPN = physicalPalletNumber
            }

            if (isShowSamplePalletNumber && samplePalletNumber) {
                PPN = samplePalletNumber
            }

            if (
                (!pallet?.Location.RequestPhysicalNumber ||
                    pallet?.Location.RequestPhysicalNumber === 'carts' ||
                    (pallet?.Location.RequestPhysicalNumber === 'optional_pallet' && !physicalPalletNumber)) &&
                physicalCart1Number
            ) {
                PC1N = physicalCart1Number
            }

            if (
                (!pallet?.Location.RequestPhysicalNumber ||
                    pallet?.Location.RequestPhysicalNumber === 'carts' ||
                    (pallet?.Location.RequestPhysicalNumber === 'optional_pallet' && !physicalPalletNumber)) &&
                physicalCart2Number
            ) {
                PC2N = physicalCart2Number
            }

            let actualEpochTime: number | undefined = undefined
            if (actualDateTime && pallet?.Location?.TimeZone) {
                actualEpochTime = Utils.time.fromDateTimePicker(actualDateTime, pallet?.Location?.TimeZone)
            }

            if (pallet?.Location.RequestPhysicalNumber === 'pallets') {
                physicalPallets = physicalPalletNumbers.map(physicalPallet => {
                    if (
                        physicalPallet.type === Enums.PalletType.EXISTING ||
                        (physicalPallet.type === Enums.PalletType.NEW &&
                            physicalPallet.palletFilling === Enums.PalletFillingType.FULL)
                    ) {
                        physicalPallet.newLocation = newLocation
                    }

                    return physicalPallet
                })

                const existingPallets = physicalPalletNumbers.filter(
                    physicalPallet => physicalPallet.type === Enums.PalletType.EXISTING
                )

                if (existingPallets.length) {
                    try {
                        if (!pallet?.Batch?.BatchId || !pallet?.Location?.LocationName) {
                            console.error('Incorrect data for the current pallet')
                            toast.error('Incorrect data for the current pallet')
                            return
                        }
                        setLoading(true)
                        const pallets = await Services.API.WidgetsApi.getPalletsByBatchId(
                            pallet?.Batch?.BatchId,
                            { nodeName: pallet?.Location?.LocationName }
                        )

                        const part1existingPallets: {
                            action: string
                            newLocation: string | undefined
                            palletFilling: any
                            palletLogicalNumber: any
                            type: Enums.PalletType
                            ppn: string
                        }[] = []

                        for (const existingPallet of existingPallets) {
                            const part1existingPallet = pallets.find(_ => _.PhysicalPalletNumber === existingPallet.ppn)

                            if (part1existingPallet) {
                                part1existingPallets.push({
                                    action: 'setNewLocationOnly',
                                    newLocation: existingPallet.newLocation,
                                    palletFilling: part1existingPallet.Filling,
                                    palletLogicalNumber: part1existingPallet.PalletNumber,
                                    type: existingPallet.type,
                                    ppn: existingPallet.ppn
                                })
                            } else {
                                const text = `Adding products to a pallet with physical number ${existingPallet.ppn} is not possible. Please remove it from the list`
                                console.error(text)
                                toast.error(text)
                                setLoading(false)
                                return
                            }
                        }

                        physicalPallets.push(...part1existingPallets)
                    } catch (error) {
                        toast.error((error as Error).message)
                        setLoading(false)
                        return
                    }
                }
            }

            let data = {
                IsForceHidden: 0,
                newLocation,
                actualEpochTime: actualEpochTime,
                physicalPalletNumber: PPN,
                physicalCart1Number: PC1N,
                physicalCart2Number: PC2N,
                physicalPallets: physicalPallets
            }

            if (pallet?.Location.RequestPhysicalNumber === 'pallets') {
                if (!physicalPallets || !physicalPallets.length) {
                    return
                }
                if (physicalPallets?.length > 1) {
                    data = { ...data, newLocation: '' }
                } else if (physicalPallets?.length === 1) {
                    data = {
                        ...data,
                        ...{ palletFilling: physicalPallets[0]?.palletFilling },
                        physicalPalletNumber: physicalPallets[0].ppn,
                        physicalPallets: undefined
                    }
                }
            } else if (
                anotherPartOfPhysicalPallet &&
                newLocation !== 'Collect Sample' &&
                PPN &&
                !isShowPhysicalPalletNumbers
            ) {
                const logicalPallets = [
                    {
                        action: 'setNewLocationOnly',
                        newLocation: newLocation,
                        palletLogicalNumber: pallet.PalletNumber,
                        type: Enums.PalletType.EXISTING,
                        ppn: PPN
                    },
                    {
                        action: 'setNewLocationOnly',
                        newLocation: newLocation,
                        palletLogicalNumber: anotherPartOfPhysicalPallet.PalletNumber,
                        type: Enums.PalletType.EXISTING,
                        ppn: PPN
                    }
                ]
                data = {
                    ...data,
                    physicalPallets: [...logicalPallets],
                    newLocation: '',
                    physicalPalletNumber: undefined
                }
            }

            setLoading(true)

            Services.API.WidgetsApi.updatePallet(pallet.PalletId, data)
                .then(() => Services.API.WidgetsApi.getPallets(isMovement))
                .then(response => {
                    dispatch(setPallets(response.pallets))
                    onMoved()
                })
                .catch(error => {
                    toast.error((error as Error).message)
                })
                .finally(() => {
                    setLoading(false)
                    modalRef.current?.close()
                    if (isMovement) {
                        dispatch(PalletSelectorActions.setPallet(undefined))
                    }
                })
        }, [
            newLocation,
            isMovement,
            onMoved,
            pallet,
            physicalPalletNumber,
            physicalCart1Number,
            physicalCart2Number,
            actualDateTime,
            samplePalletNumber,
            physicalPalletNumbers,
            anotherPartOfPhysicalPallet
        ])

        const actions = useMemo<Components.IAction[]>(
            () => [
                {
                    title: newLocation === 'Collect Sample' ? 'Collect Samples' : 'Move',
                    variant: 'primary',
                    func: move,
                    disabled: isDisabledMoveButton
                },
                {
                    title: 'Cancel',
                    variant: 'cancel',
                    func: () => {
                        if (isMovement) {
                            dispatch(PalletSelectorActions.setPallet(undefined))
                        }
                        modalRef.current?.close()
                    }
                }
            ],
            [move, isMovement, isDisabledMoveButton, newLocation]
        )

        useImperativeHandle(ref, () => ({
            async open(_pallet: Types.Pallet) {
                setPallet(_pallet)
                setPhysicalPalletNumber(_pallet.PalletPhysicalID ?? '')
                setPhysicalPalletNumbers([
                    {
                        ppn: _pallet.PalletPhysicalID ?? '',
                        type: Enums.PalletType.NEW,
                        palletFilling: _pallet.PalletFilling
                    }
                ])
                setSamplePalletNumber('')
                setPhysicalCart1Number(_pallet.Cart1 ?? '')
                setPhysicalCart2Number(_pallet.Cart2 ?? '')
                setActualDateTime(Utils.time.toDateTimePicker(Utils.time.current(), _pallet?.Location?.TimeZone))

                const validOptions = Object.keys(_pallet.Location?.ValidNextLocations ?? {}).map(key => ({
                    value: key,
                    label: _pallet.Location?.ValidNextLocations ? _pallet.Location?.ValidNextLocations[key] : ''
                }));
                setNewLocation(validOptions?.length === 1 ? validOptions[0].value : undefined)
                setValidNextLocationsOptions(validOptions)
                setAnotherPartOfPhysicalPallet(undefined)

                if (
                    _pallet.PalletPhysicalID &&
                    _pallet.Location.RequestPhysicalNumber !== 'pallets' &&
                    (_pallet.PalletFilling === Enums.PalletFillingType.BOTTOM ||
                        _pallet.PalletFilling === Enums.PalletFillingType.TOP)
                ) {

                    if (!_pallet?.Batch?.BatchId || !_pallet?.Location?.LocationName) {
                        console.error('Incorrect data for the current pallet')
                        toast.error('Incorrect data for the current pallet')
                        return
                    }
                    setLoading(true)
                    Services.API.WidgetsApi.getPalletsByBatchId(
                        _pallet?.Batch?.BatchId,
                        { nodeName: _pallet?.Location?.LocationName, ppn: _pallet.PalletPhysicalID }
                    )
                        .then(pallets => {
                            if (pallets?.length) {
                                setAnotherPartOfPhysicalPallet(pallets.find(_ => _.PalletNumber !== _pallet.PalletNumber))
                            }
                        })
                        .catch(e => {
                            console.error('Error get physical pallet data ', e)
                            toast.error('Error get physical pallet data')
                            modalRef.current?.close()
                        })
                        .finally(() => setLoading(false))
                }

                modalRef.current?.open()
            },
            close() {
                modalRef.current?.close()
            }
        }))

        const onChangeNewLocationHandler = useCallback((value: ValueType<OptionType, boolean>): void => {
            setNewLocation(value === null ? undefined : (value as OptionType).value)
        }, [])

        const onChangeActualDateTime = (date: Date | null) => {
            if (!date) {
                return
            }
            setActualDateTime(date)
        }

        const checkAndRestoreActualDateTime = (): boolean => {
            if (
                !actualDateTime ||
                (minDateTime && actualDateTime.getTime() < minDateTime.getTime()) ||
                (maxDateTime && actualDateTime.getTime() > maxDateTime.getTime())
            ) {
                setActualDateTime(Utils.time.toDateTimePicker(Utils.time.current(), pallet?.Location?.TimeZone || ''))
                toast.error('Incorrect date/time, date/time replaced with the current one')
                return false
            }

            return true
        }

        const onCalendarClose = () => {
            checkAndRestoreActualDateTime()
        }

        const setPhysicalPalletNumberByIndex = (index: number, value: string): void => {
            setPhysicalPalletNumbers(prev => {
                prev[index].ppn = value
                return [...prev]
            })
        }

        const excludePalletFromTheList = (index: number): void => {
            setPhysicalPalletNumbers(prev => {
                prev.splice(index, 1)
                return [...prev]
            })
        }

        const addNewPallet = () => {
            setPhysicalPalletNumbers(prev => {
                prev.push({ ppn: '', type: Enums.PalletType.NEW, palletFilling: Enums.PalletFillingType.FULL })
                return [...prev]
            })
        }

        const getHalfFillingPhysicalPallets = async (batchId: string, nodeName: string) => {
            try {
                const pallets = await Services.API.WidgetsApi.getPalletsByBatchId(batchId, { nodeName })
                const numbersOfAddedPhysicalPallets = physicalPalletNumbers.map(_ => _.ppn)
                return pallets?.filter(pallet => !numbersOfAddedPhysicalPallets.includes(pallet.PhysicalPalletNumber))
            } catch (e) {
                toast.error((e as Error).message)
                console.log((e as Error).message)
            }
        }

        const addToExistingPallet = (ppn: string, palletFilling: Enums.PalletFillingType, notes: string) => {
            setPhysicalPalletNumbers(prev => {
                prev.push({ ppn: ppn, type: Enums.PalletType.EXISTING, palletFilling: palletFilling, notes: notes })
                return [...prev]
            })
        }

        const setFilling = (index: number, palletFilling: Enums.PalletFillingType) => {
            setPhysicalPalletNumbers(prev => {
                prev[index].palletFilling = palletFilling
                return [...prev]
            })
        }

        const colourStyles: StylesConfig<any, any> = {
            input: styles => ({ ...styles, ...{ fontSize: '14px' } }),
            option: styles => ({ ...styles, ...{ fontSize: '14px' } }),
            placeholder: styles => ({ ...styles, ...{ fontSize: '14px' } }),
            singleValue: styles => ({ ...styles, ...{ fontSize: '14px' } })
        }

        return (
            <Components.Modal
                ref={modalRef}
                actions={actions}
                width={400}
                title="Set new location"
                testId="new-location-modal"
            >
                {pallet && (
                    <div className={`${s.Pallet} ${s.Pallet__inModal}`}>
                        {isLoading && <Components.Loader className={s.Pallet__loader} />}
                        <div className={s.Pallet__column}>
                            <div className={s.Pallet__row}>
                                <div className={s.Pallet__title}>
                                    <span className="Pallet__title__tooltip">
                                        {palletTitle}
                                    </span>
                                </div>
                                <Tooltip
                                    anchorSelect={'.Pallet__title__tooltip'}
                                    content={palletTitle}
                                />
                                {pallet?.CurrentState === Enums.PalletState.Alarm && (
                                    <img
                                        className={s.Pallet__alarmContainer}
                                        src={Assets.Alarms.AlarmTriangle}
                                        alt={''}
                                    />
                                )}
                                {pallet?.CurrentState === Enums.PalletState.Warning && (
                                    <img
                                        className={s.Pallet__alarmContainer}
                                        src={Assets.Alarms.WarningTriangle}
                                        alt={''}
                                    />
                                )}
                                {renderTime(s.Pallet__tor_cpd, pallet.Tor, 'TOR')}
                                {renderTime(s.Pallet__tor_cpd, pallet.Cpd, 'CPD')}
                            </div>
                            <div className={s.Pallet__fullItem}>
                                <span>Current Location</span>
                                <div className={styles.MoveModal__text}>
                                    {pallet.Location.Label ?? pallet.Location.LocationName ?? '--'}
                                </div>
                            </div>
                            <div className={s.Pallet__fullItem}>
                                <span>New Location</span>
                                <Select
                                    styles={colourStyles}
                                    options={validNextLocationsOptions}
                                    onChange={onChangeNewLocationHandler}
                                    defaultValue={validNextLocationsOptions?.length === 1 ? validNextLocationsOptions[0] : null}
                                />
                            </div>

                            {pallet?.Location.RequestPhysicalNumber === 'optional_pallet' &&
                                !pallet.PalletPhysicalID && (
                                    <div className={s.Pallet__fullItem}>
                                        Enter physical pallet number if cart contents have been moved to a pallet
                                    </div>
                                )}

                            {isShowPhysicalPalletNumber && !isShowSamplePalletNumber && (
                                <div className={s.Pallet__fullItem}>
                                    <span>Physical Pallet Number</span>
                                    <Components.Input
                                        type={'text'}
                                        value={physicalPalletNumber}
                                        placeholder={'Physical Pallet Number'}
                                        name={'physicalPalletNumber'}
                                        autoComplete={'off'}
                                        disabled={isDisabledPhysicalPalletNumber}
                                        onChange={e => setPhysicalPalletNumber(e.target.value)}
                                    />
                                </div>
                            )}

                            {isShowSamplePalletNumber && (
                                <div className={s.Pallet__fullItem}>
                                    <span>Sample Pallet Number</span>
                                    <Components.Input
                                        type={'text'}
                                        value={samplePalletNumber}
                                        placeholder={'Sample Pallet Number'}
                                        name={'samplePalletNumber'}
                                        autoComplete={'off'}
                                        onChange={e => setSamplePalletNumber(e.target.value)}
                                    />
                                </div>
                            )}

                            {isShowPhysicalPalletNumbers &&
                                physicalPalletNumbers.map((physicalPallet, index) => {
                                    return <div key={`ppn_${index}`} className={s.Pallet__fullItem}>
                                            <span className={s.Pallet__width145px}>
                                                {physicalPallet.type === Enums.PalletType.NEW && 'New '}
                                                {physicalPallet.type === Enums.PalletType.EXISTING && 'Existing '}
                                                Physical
                                                <br /> Pallet #
                                                { index !== physicalPalletNumbers.findIndex((pp) => pp.ppn === physicalPallet.ppn) && (
                                                    <mark> not unique</mark>
                                                )}
                                            </span>
                                            <div className={s.Pallet__fillLevelButton}>
                                                <Components.Button
                                                    title={physicalPallet.palletFilling}
                                                    variant="primary"
                                                    className={s.Pallet__button}
                                                    onClick={() => {
                                                        setPalletFillingModalRef.current?.open(
                                                            index,
                                                            physicalPallet.palletFilling,
                                                            physicalPallet.type
                                                        )
                                                    }}
                                                    disabled={physicalPallet.type === Enums.PalletType.EXISTING}
                                                />
                                            </div>
                                            <div className={s.Pallet__physicalPalletNumber}>
                                                <Components.Input
                                                    type={'text'}
                                                    value={physicalPallet.ppn}
                                                    placeholder={`Physical Pallet # ${index}`}
                                                    name={`physicalPalletNumber_${index}`}
                                                    autoComplete={'off'}
                                                    onChange={e =>
                                                        setPhysicalPalletNumberByIndex(index, e.target.value)
                                                    }
                                                    disabled={physicalPallet.type === Enums.PalletType.EXISTING}
                                                />
                                            </div>
                                            {physicalPalletNumbers.length > 1 && (
                                                <Components.Button
                                                    title="-"
                                                    variant="cancel"
                                                    className={s.Pallet__button}
                                                    onClick={() => {
                                                        excludePalletFromTheList(index)
                                                    }}
                                                />
                                            )}
                                        </div>
                                })
                            }

                            {isShowPhysicalPalletNumbers && (
                                <div className={s.Pallet__fullItem}>
                                    <Components.Button
                                        title="Add to existing pallet"
                                        variant="primary"
                                        className={s.Pallet__button}
                                        onClick={async () => {
                                            addExistingPhysicalPalletModalRef.current?.open(pallet)
                                        }}
                                    />
                                    <Components.Button
                                        title="Add new pallet"
                                        variant="primary"
                                        className={s.Pallet__button}
                                        onClick={addNewPallet}
                                    />
                                </div>
                            )}

                            {isShowPhysicalCart1Number && (
                                <div className={s.Pallet__fullItem}>
                                    <span>Physical Cart 1 Number</span>
                                    <Components.Input
                                        type={'text'}
                                        value={physicalCart1Number}
                                        placeholder={'Physical Cart 1 Number'}
                                        name={'physicalCart1Number'}
                                        autoComplete={'off'}
                                        disabled={isDisabledPhysicalCart1Number}
                                        onChange={e => setPhysicalCart1Number(e.target.value)}
                                    />
                                </div>
                            )}

                            {isShowPhysicalCart2Number && (
                                <div className={s.Pallet__fullItem}>
                                    <span>
                                        Physical Cart 2 Number
                                        {physicalCart1Number && physicalCart1Number === physicalCart2Number && (
                                            <i>please type unique number</i>
                                        )}
                                    </span>
                                    <Components.Input
                                        type={'text'}
                                        value={physicalCart2Number}
                                        placeholder={'Physical Cart 2 Number'}
                                        name={'physicalCart2Number'}
                                        autoComplete={'off'}
                                        disabled={isDisabledPhysicalCart2Number}
                                        onChange={e => setPhysicalCart2Number(e.target.value)}
                                    />
                                </div>
                            )}
                            <div className={s.Pallet__fullItem}>
                                <span>Time zone</span>
                                <div className={styles.MoveModal__text}>{timeZone?.displayName}</div>
                            </div>
                            <div className={s.Pallet__fullItem}>
                                <span>Date/Time</span>
                                <div>
                                    <DatePicker
                                        selected={actualDateTime}
                                        onChange={onChangeActualDateTime}
                                        onCalendarClose={onCalendarClose}
                                        showTimeSelect
                                        timeFormat="HH:mm"
                                        timeIntervals={1}
                                        minDate={minDateTime}
                                        minTime={minDateTime}
                                        maxDate={maxDateTime}
                                        maxTime={maxDateTime}
                                        timeInputLabel="Time:"
                                        dateFormat="MM/dd/yyyy HH:mm"
                                        wrapperClassName={styles.MoveModal}
                                    />
                                </div>
                            </div>
                            {notification && (
                                <div className={s.Pallet__fullItem}>
                                    <div className={styles.MoveModal__notification}>{notification}</div>
                                </div>
                            )}
                        </div>

                        <AddExistingPhysicalPalletModal
                            ref={addExistingPhysicalPalletModalRef}
                            addToExistingPallet={addToExistingPallet}
                            getHalfFillingPhysicalPallets={getHalfFillingPhysicalPallets}
                        />
                        <SetPalletFillingModal ref={setPalletFillingModalRef} setFilling={setFilling} />
                    </div>
                )}
            </Components.Modal>
        )
    }
)
